if (!customElements.get('media-carousel')) {
  customElements.define(
    'media-carousel',
    class MediaCarousel extends HTMLElement {
      constructor() {
        super();

        this.slider = this.querySelector('.carousel-list');
        if (!this.slider) return;

        this.sliderItems = this.querySelectorAll('.carousel-list > *');
        this.imageCount = Number(this.dataset.size);
        this.dotsBar = this.querySelector('.media-dots--bar');
        this.currentPage = 1;
        this.sliderFirstItemNode = this.sliderItems[0];

        this.prevButton = this.querySelector('[previous-image]');
        this.nextButton = this.querySelector('[next-image]');
        this.currentIndex = 0;

        this.observer = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.currentIndex = entry.target.index;
                document.dispatchEvent(new CustomEvent('custom:carousel:index', { detail: entry.target.index }));
              }
            });
          },
          {
            threshold: 0.51,
          },
        );

        this.sliderItems.forEach((sliderItem, index) => {
          sliderItem.index = index;
          this.observer.observe(sliderItem);
        });

        if (this.dataset.desktopTouch) {
          this.initDesktopTouch();
        }

        const shownPeek = localStorage.getItem('shownPeek')

        if(this.dataset.first === "true" && !shownPeek) {
          this.peekObserver = new IntersectionObserver(
            (entries, observer) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) {
                  this.initPeek();
                }
              });
            },
            {
              threshold: 1,
            },
          );

          this.peekObserver.observe(this);
        }


        this.initSwipe();

        // Hydrate lazy carousel slides as soon as the user shows intent (hover or first touch)
        this.addEventListener('pointerenter', this.hydrate.bind(this), { once: true });

        if (this.prevButton) this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
        if (this.nextButton) this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

        this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element.clientWidth > 0);
        if (this.sliderItemsToShow.length < 2) return;
        this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft;
        if (!this.sliderItemOffset) return;
        this.slidesPerPage = Math.floor(
          (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
        );
        this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;

        this.slider.addEventListener('scroll', this.update.bind(this));
      }

      hydrate() {
        if (this.hydrated) return;
        this.hydrated = true;
        const firstImg = this.querySelector('img[srcset]');
        const sizes = firstImg ? firstImg.getAttribute('sizes') : null;
        this.querySelectorAll('img[data-base]').forEach((img) => {
          const base = img.dataset.base;
          const max = Number(img.dataset.w) || 0;
          const join = base.includes('?') ? '&' : '?';
          const srcset = [165, 360, 533, 720, 940, 1066]
            .filter((w) => w <= max)
            .map((w) => `${base}${join}width=${w} ${w}w`);
          srcset.push(`${base} ${max}w`);
          if (sizes) img.sizes = sizes;
          img.srcset = srcset.join(',');
          img.src = max >= 533 ? `${base}${join}width=533` : base;
          img.removeAttribute('data-base');
        });
        // Legacy markup (cached section HTML) still uses data-src/data-srcset
        this.querySelectorAll('img[data-src]').forEach((img) => {
          if (img.dataset.srcset) {
            img.srcset = img.dataset.srcset;
            img.removeAttribute('data-srcset');
          }
          img.src = img.dataset.src;
          img.removeAttribute('data-src');
        });
      }

      update() {
        if (!this.sliderItemOffset) return;
        this.hydrate();
        const previousPage = this.currentPage;
        this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;
        const percentageComplete = (this.currentPage / this.imageCount) * 100;
        if (this.dotsBar) this.dotsBar.style.width = `${percentageComplete}%`;

        if (this.currentPage != previousPage) {
          this.dispatchEvent(
            new CustomEvent('slideChanged', {
              detail: {
                currentPage: this.currentPage,
                currentElement: this.sliderItemsToShow[this.currentPage - 1],
              },
            })
          );
        }
      }

      onButtonClick(event) {
        if (!this.sliderItemsToShow || this.sliderItemsToShow.length === 0) return;
        this.hydrate();

        const isFirstSlide = this.currentPage === 1;
        const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

        if (event.currentTarget.name === 'next') {
          this.currentPage = this.currentPage + 1
        } else {
          this.currentPage = this.currentPage - 1
        }

        if (isFirstSlide && event.currentTarget.name === 'previous') {
          this.slideScrollPosition =
            this.slider.scrollLeft + (this.sliderFirstItemNode ? this.sliderFirstItemNode.clientWidth : 0) * this.sliderItemsToShow.length;
            this.currentPage = this.sliderItemsToShow.length;
        } else if (isLastSlide && event.currentTarget.name === 'next') {
          this.slideScrollPosition = 0;
          this.currentPage = 1;
        } else {
          const step = event.currentTarget.dataset.step || 1;
          this.slideScrollPosition =
            event.currentTarget.name === 'next'
              ? this.slider.scrollLeft + step * (this.sliderItemOffset || 0)
              : this.slider.scrollLeft - step * (this.sliderItemOffset || 0);
        }

        this.setSlidePosition(this.slideScrollPosition);
      }

      setSlidePosition(position) {
        if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
        this.setPositionTimeout = setTimeout(() => {
          this.slider.scrollTo({
            left: position,
          });
          setTimeout(() => {
            this.update();
          }, 100);
        }, 66);
      }


      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
        if (this.peekObserver) this.peekObserver.disconnect();
      }

      initPeek() {
        this.hydrate();
        let leftOffset = this.slider.getBoundingClientRect().width / 2;
        this.slider.classList.add('snap-none');
        requestAnimationFrame(() => {
          this.sliderItems.forEach((sliderItem) => sliderItem.classList.add('pointer-events-none', 'select-none'));
          this.slider.scrollLeft = leftOffset;

          setTimeout(() => {
            this.slider.scrollLeft = 0;
            setTimeout(() => {
              localStorage.setItem('shownPeek', 'true');
              this.slider.classList.remove('snap-none');
              this.sliderItems.forEach((sliderItem) => sliderItem.classList.remove('pointer-events-none', 'select-none'));
            }, 250);
          }, 250);
        });
      }

      initSwipe() {
        if (!this.slider) return;
        let startX = 0;
        let swiped = false;
        const threshold = 40;

        this.slider.addEventListener('touchstart', (e) => {
          this.hydrate();
          startX = e.touches[0].clientX;
          swiped = false;
        }, { passive: true });

        this.slider.addEventListener('touchmove', (e) => {
          if (swiped) return;
          const dx = e.touches[0].clientX - startX;
          if (Math.abs(dx) > threshold) {
            swiped = true;
            if (dx < 0) {
              this.onButtonClick({ currentTarget: this.nextButton || { name: 'next', dataset: {} } });
            } else {
              this.onButtonClick({ currentTarget: this.prevButton || { name: 'previous', dataset: {} } });
            }
          }
        }, { passive: true });
      }

      initDesktopTouch() {
        if (window.innerWidth <= 767) return;
        this.sliderItems.forEach((sliderItem) => sliderItem.classList.add('select-none'));

        let isDrag = false;
        let hasDragged = false;
        let timeout;
        const dragStart = (ev) => {
          isDrag = true;
          hasDragged = false;
          this.slider.classList.add('snap-none');
          this.sliderItems.forEach((sliderItem) => sliderItem.classList.add('pointer-events-none'));
          if (timeout) clearTimeout(timeout);
        };
        const dragEnd = () => {
          if (!isDrag) return;
          isDrag = false;
          if (this.sliderItems[this.currentIndex]) {
            this.sliderItems[this.currentIndex].scrollIntoView({ behavior: 'smooth' });
          }
          timeout = setTimeout(() => {
            this.slider.classList.remove('snap-none');
            if (!hasDragged) {
              this.sliderItems.forEach((sliderItem) => sliderItem.classList.remove('pointer-events-none'));
            }
          }, 100);
          setTimeout(() => {
            this.sliderItems.forEach((sliderItem) => sliderItem.classList.remove('pointer-events-none'));
          }, 700);
        };
        const drag = (event) => {
          if (!isDrag) return;
          hasDragged = true;
          this.slider.scrollLeft -= event.movementX * 1.6;
        };

        this.slider.addEventListener('pointerdown', dragStart);
        this.slider.addEventListener('pointerup', dragEnd);
        this.slider.addEventListener('pointerleave', dragEnd);
        this.slider.addEventListener('pointermove', drag);
      }

    }
  );
}
